/**
 * Provides an administration interface for managing application theme
 * availability and appearance settings. Allows administrators to enable
 * or disable supported themes, including Light, Dark, and Vibrant themes,
 * while supporting configuration updates, change tracking, and restoration
 * of unsaved theme preferences.
 */
import React, { useEffect, useState } from "react";

import { getCommonConfig, updateCommonConfig } from "@/services/commonconfig";

const Toggle = ({ value, onChange }: any) => (
  <div className="flex border border-gray-300 rounded overflow-hidden">
    <button
      className={`px-3 py-1 text-xs ${
        value ? "bg-[var(--color-brand)]  text-white" : "bg-gray-100 text-gray-600"
      }`}
      onClick={() => onChange(true)}
    >
      Show
    </button>
    <button
      className={`px-3 py-1 text-xs ${
        !value ? "bg-[var(--color-brand)]  text-white" : "bg-gray-100 text-gray-600"
      }`}
      onClick={() => onChange(false)}
    >
      Hide
    </button>
  </div>
);

const ThemesConfig: React.FC = () => {
  const [data, setData] = useState<any>({});
  const [initial, setInitial] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    const res = await getCommonConfig();
    setData(res);
    setInitial(JSON.stringify(res));
  };

  const updateField = (key: string, val: boolean) => {
    setData((prev: any) => ({ ...prev, [key]: val }));
  };

  const hasChanges = JSON.stringify(data) !== initial;

  const handleSave = async () => {
    setSaving(true);
    await updateCommonConfig(data);
    setInitial(JSON.stringify(data));
    setSaving(false);
  };

  const handleCancel = () => {
    setData(JSON.parse(initial));
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-3">
      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">Light Theme</span>
        <Toggle value={data.LightTheme} onChange={(v: any) => updateField("LightTheme", v)} />
      </div>

      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">Dark Theme</span>
        <Toggle value={data.DarkTheme} onChange={(v: any) => updateField("DarkTheme", v)} />
      </div>

      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">Vibrant Theme</span>
        <Toggle value={data.VibrantTheme} onChange={(v: any) => updateField("VibrantTheme", v)} />
      </div>

      <div className="flex justify-end gap-2 items-center pt-2">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 text-xs"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)]  text-white px-3 py-1 text-xs"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default ThemesConfig;
